import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { canViewAllData } from '../utils/permissions';
import Tooltip from '../components/common/Tooltip';

const AdminOverviewPage = () => {
    const { currentUser } = useAuth();
    
    // Check if user is admin or viewer
    if (!currentUser || !canViewAllData(currentUser)) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-semibold">Access Denied</p>
                    <p className="text-red-600 text-sm mt-1">
                        You don't have permission to access administration features. This area is admin-only.
                    </p>
                </div>
            </main>
        );
    }

    const adminFeatures = [
        {
            id: 'users',
            title: 'User Administration',
            description: 'Manage user accounts, roles, and permissions. Create new users, deactivate accounts, and update user profiles.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none">
                    <circle cx="8" cy="7" r="3" fill="#A78BFA" stroke="#7C3AED" strokeWidth="1"/>
                    <circle cx="16" cy="7" r="3" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
                    <path d="M3 18c0-2.5 2-4 5-4s5 1.5 5 4v2H3v-2z" fill="#C4B5FD" stroke="#7C3AED" strokeWidth="1"/>
                    <path d="M11 18c0-2.5 2-4 5-4s5 1.5 5 4v2h-10v-2z" fill="#BFDBFE" stroke="#2563EB" strokeWidth="1"/>
                </svg>
            ),
            color: 'indigo',
            link: '/admin/users',
            tooltip: 'Manage user accounts, view login activity, and assign roles'
        },
        {
            id: 'roles',
            title: 'Role Management',
            description: 'Configure roles and permissions. Define what each role can access and modify within the system.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none">
                    <circle cx="7" cy="7" r="4" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5"/>
                    <circle cx="7" cy="7" r="2" fill="none" stroke="#F59E0B" strokeWidth="1.5"/>
                    <rect x="9" y="6" width="10" height="2" rx="1" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1"/>
                    <rect x="15" y="8" width="1.5" height="2" fill="#F59E0B"/>
                    <rect x="17.5" y="8" width="1.5" height="2.5" fill="#F59E0B"/>
                </svg>
            ),
            color: 'amber',
            link: '/admin/roles',
            tooltip: 'Configure role permissions and access controls'
        },
        {
            id: 'notifications',
            title: 'Notification Settings',
            description: 'Configure system-wide notification rules, deadline alerts, and email triggers for the organization.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none">
                    <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 17v1a3 3 0 106 0v-1" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="18" cy="6" r="3" fill="#EF4444" stroke="#B91C1C" strokeWidth="1"/>
                </svg>
            ),
            color: 'yellow',
            link: '/admin/notifications',
            tooltip: 'Configure deadline notifications and email alert settings'
        },
        {
            id: 'logs',
            title: 'Log Management',
            description: 'View and manage system logs. Configure retention periods and clean up old log entries.',
            icon: <span className="text-3xl">📋</span>,
            color: 'emerald',
            link: '/admin/logs',
            tooltip: 'Manage system logs and retention policies'
        },
        {
            id: 'workflows',
            title: 'Workflow Automation',
            description: 'Create and manage automated workflows. Configure triggers, actions, and conditions for business processes.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" viewBox="0 0 24 24" fill="none">
                    <circle cx="6" cy="6" r="3" fill="#C4B5FD" stroke="#7C3AED" strokeWidth="1.5"/>
                    <circle cx="18" cy="6" r="3" fill="#BAE6FD" stroke="#0284C7" strokeWidth="1.5"/>
                    <circle cx="12" cy="18" r="3" fill="#FED7AA" stroke="#EA580C" strokeWidth="1.5"/>
                    <path d="M6 9v3h12V9" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                    <path d="M12 12v3" stroke="#6B7280" strokeWidth="1.5"/>
                </svg>
            ),
            color: 'purple',
            link: '/admin/workflows',
            tooltip: 'Configure automated task creation and workflow triggers'
        },
        {
            id: 'settings',
            title: 'System Settings',
            description: 'Manage email templates, script variables, and system configurations used by background processes.',
            icon: <span className="text-3xl">🔧</span>,
            color: 'gray',
            link: '/admin/system-settings',
            tooltip: 'Manage email templates, script variables, and configurations'
        },
        {
            id: 'chatbot',
            title: 'Chatbot Admin',
            description: 'Manage chatbot settings, conversation history, and AI response configurations.',
            icon: <span className="text-3xl">🤖</span>,
            color: 'blue',
            link: '/admin/chatbot',
            tooltip: 'Configure chatbot behavior and view conversation analytics'
        },
        {
            id: 'knowledge',
            title: 'Knowledge Management',
            description: 'Manage knowledge base articles, FAQs, and documentation for the chatbot and help system.',
            icon: <span className="text-3xl">📚</span>,
            color: 'teal',
            link: '/admin/knowledge',
            tooltip: 'Create and organize knowledge base content'
        },
        {
            id: 'achievements',
            title: 'Achievement Management',
            description: 'Configure achievements, point values, and gamification settings for user engagement.',
            icon: <span className="text-3xl">🏆</span>,
            color: 'rose',
            link: '/admin/achievements',
            tooltip: 'Manage achievements and gamification rewards'
        }
    ];

    const getColorClasses = (color) => {
        const colors = {
            indigo: { border: 'border-indigo-500', text: 'text-indigo-600', hover: 'hover:text-indigo-700' },
            amber: { border: 'border-amber-500', text: 'text-amber-600', hover: 'hover:text-amber-700' },
            yellow: { border: 'border-yellow-500', text: 'text-yellow-600', hover: 'hover:text-yellow-700' },
            emerald: { border: 'border-emerald-500', text: 'text-emerald-600', hover: 'hover:text-emerald-700' },
            purple: { border: 'border-purple-500', text: 'text-purple-600', hover: 'hover:text-purple-700' },
            gray: { border: 'border-gray-500', text: 'text-gray-600', hover: 'hover:text-gray-700' },
            blue: { border: 'border-blue-500', text: 'text-blue-600', hover: 'hover:text-blue-700' },
            teal: { border: 'border-teal-500', text: 'text-teal-600', hover: 'hover:text-teal-700' },
            rose: { border: 'border-rose-500', text: 'text-rose-600', hover: 'hover:text-rose-700' },
            cyan: { border: 'border-cyan-500', text: 'text-cyan-600', hover: 'hover:text-cyan-700' }
        };
        return colors[color] || colors.gray;
    };

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Administration</h2>
                <p className="text-gray-600">
                    Manage system settings, users, roles, and automated workflows. 
                    These features are only available to administrators.
                </p>
            </div>

            {/* Admin Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adminFeatures.map(feature => {
                    const colorClasses = getColorClasses(feature.color);
                    
                    return (
                        <Tooltip key={feature.id} text={feature.tooltip}>
                            <Link to={feature.link}>
                                <div className={`bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 ${colorClasses.border} cursor-pointer group h-full`}>
                                    <div className="flex flex-col h-full">
                                        <div className="flex-shrink-0 mb-4">
                                            {feature.icon}
                                        </div>
                                        <h4 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h4>
                                        <p className="text-sm text-gray-600 mb-4 flex-grow">{feature.description}</p>
                                        <div className={`${colorClasses.text} font-medium flex items-center group-hover:translate-x-1 transition-transform`}>
                                            Open
                                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </Tooltip>
                    );
                })}
            </div>

            {/* Quick Stats */}
            <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Quick Access</h3>
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Link to="/admin/users" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <span className="text-2xl">👥</span>
                            <div>
                                <div className="font-medium text-gray-900">Add New User</div>
                                <div className="text-sm text-gray-500">Create a new user account</div>
                            </div>
                        </Link>
                        <Link to="/admin/workflows" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <span className="text-2xl">⚡</span>
                            <div>
                                <div className="font-medium text-gray-900">Create Workflow</div>
                                <div className="text-sm text-gray-500">Set up a new automation</div>
                            </div>
                        </Link>
                        <Link to="/admin/system-settings" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <span className="text-2xl">📝</span>
                            <div>
                                <div className="font-medium text-gray-900">Add System Setting</div>
                                <div className="text-sm text-gray-500">Create variable, email template, or config</div>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default AdminOverviewPage;
